// Rectangular hex grid storage. Uses axial coords internally.
// For a 7-wide, 5-tall pointy-top grid we use odd-r offset semantics:
// col = q + (r - (r & 1)) / 2 ; row = r
// Storage is keyed by `${q},${r}` so arbitrary axial coords work.

import { hexKey, type HexCoord } from "./HexCoord";

export class HexGrid<T> {
  private cells = new Map<string, T>();

  constructor(
    public readonly cols: number,
    public readonly rows: number,
  ) {}

  // Iterate all in-bounds coords (odd-r offset rectangle).
  *coords(): IterableIterator<HexCoord> {
    for (let row = 0; row < this.rows; row++) {
      for (let col = 0; col < this.cols; col++) {
        yield HexGrid.offsetToAxial(col, row);
      }
    }
  }

  inBounds(h: HexCoord): boolean {
    const { col, row } = HexGrid.axialToOffset(h);
    return col >= 0 && col < this.cols && row >= 0 && row < this.rows;
  }

  get(h: HexCoord): T | undefined {
    return this.cells.get(hexKey(h));
  }

  set(h: HexCoord, value: T): void {
    this.cells.set(hexKey(h), value);
  }

  delete(h: HexCoord): void {
    this.cells.delete(hexKey(h));
  }

  has(h: HexCoord): boolean {
    return this.cells.has(hexKey(h));
  }

  clear(): void {
    this.cells.clear();
  }

  entries(): IterableIterator<[string, T]> {
    return this.cells.entries();
  }

  // Offset (col,row) <-> axial (q,r) for odd-r pointy-top.
  static offsetToAxial(col: number, row: number): HexCoord {
    const q = col - ((row - (row & 1)) >> 1);
    return { q, r: row };
  }

  static axialToOffset(h: HexCoord): { col: number; row: number } {
    const col = h.q + ((h.r - (h.r & 1)) >> 1);
    return { col, row: h.r };
  }
}
